// /en/police-clearance-foreigner — EN Hub for foreigners in Thailand
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { createFileRoute, Link } from "@tanstack/react-router";
import { FOREIGNER_NATIONALITIES } from "@/data/police-foreigner-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { ShieldCheck, Globe2, MessageCircle, Phone, MapPin, BadgeCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/police-clearance-foreigner/")({
  head: () => {
    const url = `${SITE}/en/police-clearance-foreigner`;
    const title = "Home-Country Police Clearance for Foreigners in Thailand (25+ Nationalities) | NYC";
    const desc = "Full-service procurement of home-country police clearance (FBI, ACRO, AFP, RCMP, Führungszeugnis. Get your timeline and checklist in minutes — chat now.";
    return {
      meta: [
        { title: ctaTitle(title, "/en/police-clearance-foreigner", "en") },
        { name: "description", content: ctaDesc(desc, "/en/police-clearance-foreigner", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/police-clearance-foreigner", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/police-clearance-foreigner", "en")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_US" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/police-clearance-foreigner", "/en/police-clearance-foreigner")],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: "Home-Country Police Clearance for Foreigners in Thailand",
              serviceType: "Foreign Police Clearance Procurement (in Thailand)",
              areaServed: { "@type": "Country", name: "Thailand" },
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: 5500, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
        ]),
      ],
    };
  },
  component: HubEnPage,
});

function HubEnPage() {
  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link><span className="px-1.5 text-white/30">/</span>
            <Link to="/en/police-clearance" className="hover:text-gold">Police Clearance</Link><span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">For Foreigners in Thailand</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ShieldCheck className="h-3.5 w-3.5" /> Foreign Police Clearance · in Thailand
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            Home-Country Police Clearance for Foreigners Living in Thailand
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            End-to-end procurement of FBI, ACRO, AFP, RCMP, Führungszeugnis and equivalents for {FOREIGNER_NATIONALITIES.length}+ nationalities — including Apostille/Embassy legalization + certified Thai translation with MFA. Used for Thai Immigration Non-B, Work Permit, marriage registration, and PR.
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> +66 83-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          NYC procures home-country Police Clearance for foreign nationals residing in Thailand — covering {FOREIGNER_NATIONALITIES.length}+ nationalities. Full chain (PC → Apostille/Embassy legalization → certified Thai translation + MFA) from THB 5,500, 18–45 days, handled by Lawyers Council-registered attorneys.
        </blockquote>

        <section className="mt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <Globe2 className="mr-2 inline-block h-6 w-6 text-amber-600" />
            Select Your Nationality ({FOREIGNER_NATIONALITIES.length})
          </h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {FOREIGNER_NATIONALITIES.map((n) => (
              <a key={n.slug} href={`/police-clearance-foreigner/${n.slug}`}
                className="group rounded-2xl border border-slate-200 bg-white p-4 hover:border-gold/50 hover:shadow-md transition">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 shrink-0 text-amber-600" />
                  <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                    {n.nameEn} ({n.countryEn})
                  </div>
                </div>
                <div className="mt-1.5 text-[12px] text-slate-600 line-clamp-2">{n.homeAuthorityEn}</div>
                <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                  <span>From THB {n.priceFromTh.toLocaleString()}</span>
                  <span>{n.timelineDays}</span>
                </div>
              </a>
            ))}
          </div>
        </section>

        <section className="mt-14"><ContactCTA /></section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">FAQ</h2>
          <div className="mt-5">
            <FaqAccordion
              items={[
                { q: "Do I need to fly home to obtain a Police Clearance?", a: "Not in most cases — NYC coordinates via online portals (UK ACRO, US FBI Channeler, AU AFP, NZ MoJ, DE Konsulat) or through the embassy in Bangkok where permitted." },
                { q: "Can Thai Immigration accept my home-country PC directly?", a: "No. It must first pass Apostille or Embassy Legalization in the country of origin, then certified Thai translation with MFA endorsement." },
                { q: "What is the fastest turnaround?", a: "The fastest end-to-end turnaround is 18 business days for digitalized systems such as UK ACRO Police Certificate and Australian AFP National Police Check, including Apostille or embassy legalization plus certified Thai translation and MFA. Non-digitalized systems (Russia MVD, Japan Koseki-shozai, Singapore SPF) may take 30-70 days due to manual clearance." },
                { q: "How many nationalities do you cover?", a: `${FOREIGNER_NATIONALITIES.length}+ core nationalities. Other jurisdictions (Africa, Middle East, Latin America) available on request.` },
              ]}
            />
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
      <QuotableStatsStrip cluster="police" locale="en" variant="light" />
    <RelatedHubGrid locale="en" /></main>
  );
}
